const monoFont = "'IBM Plex Mono', monospace";

function SkeletonBlock({ width = '100%', height = 16, style }: { width?: string | number; height?: number; style?: React.CSSProperties }) {
  return (
    <div
      className="animate-pulse"
      style={{
        width,
        height,
        backgroundColor: 'rgba(247,246,242,0.06)',
        borderRadius: 2,
        ...style,
      }}
    />
  );
}

/** Full-page skeleton shown while verifying auth/ownership in AdminDashboard */
export function CmsDashboardSkeleton() {
  return (
    <div style={{ position: 'fixed', inset: 0, width: '100vw', height: '100vh', backgroundColor: '#0A0A0A', overflow: 'hidden', display: 'flex' }}>
      {/* Sidebar skeleton */}
      <aside style={{ width: 240, height: '100vh', backgroundColor: 'rgba(247,246,242,0.06)', borderRight: '0.5px solid rgba(247,246,242,0.08)', flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Identity */}
        <div style={{ padding: '24px 24px 16px 24px', borderBottom: '0.5px solid rgba(247,246,242,0.08)' }}>
          <SkeletonBlock width={80} height={14} />
        </div>
        {/* Nav items */}
        <div style={{ flex: 1, padding: '16px 0', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {[...Array(6)].map((_, i) => (
            <div key={i} style={{ padding: '10px 24px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <SkeletonBlock width={16} height={16} />
              <SkeletonBlock width={80 + i * 10} height={13} />
            </div>
          ))}
          <div style={{ height: 1, backgroundColor: 'rgba(247,246,242,0.08)', margin: '4px 16px' }} />
          {[...Array(3)].map((_, i) => (
            <div key={i} style={{ padding: '10px 24px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <SkeletonBlock width={16} height={16} />
              <SkeletonBlock width={90 + i * 5} height={13} />
            </div>
          ))}
        </div>
      </aside>

      {/* Main content skeleton */}
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Topbar */}
        <div style={{ height: 48, borderBottom: '0.5px solid rgba(247,246,242,0.08)', display: 'flex', alignItems: 'center', padding: '0 24px' }}>
          <SkeletonBlock width={120} height={12} />
        </div>
        {/* Content */}
        <div style={{ flex: 1, padding: 32, display: 'flex', flexDirection: 'column', gap: 24 }}>
          <SkeletonBlock width={200} height={14} />
          <div style={{ display: 'flex', gap: 16 }}>
            {[...Array(3)].map((_, i) => (
              <div key={i} style={{ flex: 1, border: '0.5px solid rgba(247,246,242,0.08)', padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
                <SkeletonBlock width="60%" height={11} />
                <SkeletonBlock width="40%" height={28} />
                <SkeletonBlock width="50%" height={10} />
              </div>
            ))}
          </div>
          <div style={{ border: '0.5px solid rgba(247,246,242,0.08)', padding: 24 }}>
            <SkeletonBlock width={160} height={11} style={{ marginBottom: 16 }} />
            <SkeletonBlock width="100%" height={180} />
          </div>
        </div>
      </main>
    </div>
  );
}

/** Skeleton for project list (AdminProjects loading state) */
export function CmsProjectsSkeleton() {
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <SkeletonBlock width={120} height={14} />
        <SkeletonBlock width={90} height={28} />
      </div>
      <div className="flex flex-col gap-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} style={{ border: '0.5px solid rgba(247,246,242,0.08)', padding: 24 }}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 flex flex-col gap-2">
                <SkeletonBlock width={180 + i * 20} height={16} />
                <SkeletonBlock width={70} height={18} />
                <SkeletonBlock width="80%" height={12} />
              </div>
              <div className="flex items-center gap-3">
                {[...Array(4)].map((_, j) => (
                  <SkeletonBlock key={j} width={14} height={14} />
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Skeleton for home gallery loading */
export function CmsHomeGallerySkeleton() {
  return (
    <div>
      <SkeletonBlock width={120} height={14} style={{ marginBottom: 16 }} />
      {/* Layout selector */}
      <div className="flex gap-3 mb-6">
        {[...Array(4)].map((_, i) => (
          <SkeletonBlock key={i} width={60} height={60} />
        ))}
      </div>
      {/* Image grid */}
      <div className="flex flex-col gap-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} style={{ border: '0.5px solid rgba(247,246,242,0.08)', padding: 16, display: 'flex', gap: 12 }}>
            <SkeletonBlock width={120} height={80} />
            <div className="flex-1 flex flex-col gap-2">
              <SkeletonBlock width="60%" height={13} />
              <SkeletonBlock width="40%" height={11} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
